import { useState, useEffect } from "react";
import {
  getExecutiveSummary,
  getCampaigns,
} from "../api/DownloadsApi";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Download, FileText, PieChart, BarChart3,
  TrendingUp, Users, Shield, AlertTriangle,
  Clock, CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import CulsightLoader from "../components/ui/CulsightLoader";

const Downloads = () => {
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchExecutiveSummary();
  }, []);

  const fetchExecutiveSummary = async () => {
    setLoading(true);
    try {
      const response = await getExecutiveSummary();
      setExecutiveSummary(response.data);
    } catch (error) {
      toast.error("Failed to load executive summary");
    }
    setLoading(false);
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      // Generate PDF content
      const content = generatePDFContent();

      // Create blob and download
      const blob = new Blob([content], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);

      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      printWindow.onload = () => {
        printWindow.print();
      };

      toast.success("PDF report ready for download");
    } catch (error) {
      toast.error("Failed to generate PDF");
    }
    setGenerating(false);
  };

  const generatePDFContent = () => {
    const data = executiveSummary;
    return `
<!DOCTYPE html>
<html>
<head>
  <title>Executive Security Report - ${data?.organization_name}</title>
  <style>
    body { font-family: 'Inter', sans-serif; padding: 40px; color: #1f1f23; }
    .header { text-align: center; border-bottom: 3px solid #8c3add; padding-bottom: 20px; margin-bottom: 30px; }
    .logo { font-size: 28px; font-weight: bold; }
    .logo span:first-child { color: #8c3add; }
    h1 { color: #8c3add; margin-top: 10px; }
    h2 { color: #1f1f23; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px; margin-top: 30px; }
    .metric-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 20px 0; }
    .metric-card { background: #f8fafc; padding: 20px; border-radius: 8px; text-align: center; }
    .metric-value { font-size: 32px; font-weight: bold; color: #8c3add; }
    .metric-label { color: #64748b; font-size: 14px; margin-top: 5px; }
    .risk-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
    .risk-low { background: #d1fae5; color: #065f46; }
    .risk-medium { background: #fef3c7; color: #92400e; }
    .risk-high { background: #fee2e2; color: #991b1b; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
    th { background: #f8fafc; font-weight: 600; }
    .recommendation { padding: 10px 15px; margin: 8px 0; background: #f0f9ff; border-left: 4px solid #8c3add; }
    .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #64748b; font-size: 12px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo"><span>Cul</span><span>sight</span></div>
    <h1>Executive Security Report</h1>
    <p style="color: #64748b;">Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
  </div>

  <h2>1. Overall Risk Posture</h2>
  <div class="metric-grid">
    <div class="metric-card">
      <div class="metric-value">${data?.overall_risk_posture?.total_users || 0}</div>
      <div class="metric-label">Total Users</div>
    </div>
    <div class="metric-card">
      <div class="metric-value">${data?.overall_risk_posture?.total_campaigns || 0}</div>
      <div class="metric-label">Campaigns Run</div>
    </div>
    <div class="metric-card">
      <div class="metric-value">${data?.overall_risk_posture?.overall_click_rate || 0}%</div>
      <div class="metric-label">Click Rate</div>
    </div>
  </div>
  <p>Current Risk Level: <span class="risk-badge risk-${(data?.overall_risk_posture?.risk_level || 'medium').toLowerCase().replace('-', '')}">${data?.overall_risk_posture?.risk_level || 'Medium'}</span></p>

  <h2>2. Top 5 High-Risk Departments</h2>
  <table>
    <thead>
      <tr>
        <th>Department</th>
        <th>Risk Score</th>
        <th>Phishing Fail Rate</th>
      </tr>
    </thead>
    <tbody>
      ${(data?.top_risk_departments || []).map(d => `
        <tr>
          <td>${d.name}</td>
          <td><strong>${d.risk_score}%</strong></td>
          <td>${(d.fail_rate * 100).toFixed(1)}%</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <h2>3. Trend Analysis (${data?.trend_analysis?.period || 'Last 3 months'})</h2>
  <p>
    Security awareness is <strong style="color: ${data?.trend_analysis?.direction === 'improving' ? '#059669' : '#dc2626'}">
      ${data?.trend_analysis?.direction || 'improving'}
    </strong> with a ${data?.trend_analysis?.change_percentage || 0}% ${data?.trend_analysis?.direction === 'improving' ? 'improvement' : 'decline'}.
  </p>

  <h2>4. Offender Summary</h2>
  <div class="metric-grid">
    <div class="metric-card">
      <div class="metric-value">${data?.offender_summary?.total_offenders || 0}</div>
      <div class="metric-label">Total Offenders</div>
    </div>
    <div class="metric-card">
      <div class="metric-value" style="color: #dc2626;">${data?.offender_summary?.high_risk_count || 0}</div>
      <div class="metric-label">High Risk</div>
    </div>
    <div class="metric-card">
      <div class="metric-value" style="color: #f59e0b;">${data?.offender_summary?.repeat_offenders || 0}</div>
      <div class="metric-label">Repeat Offenders</div>
    </div>
  </div>

  <h2>5. Recommended Actions</h2>
  ${(data?.recommendations || []).map((rec, i) => `
    <div class="recommendation">${i + 1}. ${rec}</div>
  `).join('')}

  <div class="footer">
    <p>This report was automatically generated by Culsight Phishing Simulation Platform</p>
    <p>© ${new Date().getFullYear()} Culsight Security - Confidential</p>
  </div>
</body>
</html>
    `;
  };

  const downloadCSV = async () => {
    try {
      const response = await getCampaigns();
      const campaigns = response.data.campaigns || [];

      const csv = [
        ["Name", "Status", "Sent", "Opened", "Clicked", "Submitted", "Reported", "User Group", "Created"],
        ...campaigns.map(c => [
          c.name,
          c.status,
          c.emails_sent,
          c.emails_opened,
          c.links_clicked,
          c.data_submitted,
          c.reported_spam,
          c.user_group,
          new Date(c.created_at).toLocaleDateString()
        ])
      ].map(row => row.join(",")).join("\n");

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `phishing_campaigns_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV downloaded");
    } catch (error) {
      toast.error("Failed to download CSV");
    }
  };

  if (loading) {
    return <CulsightLoader variant="page" />;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="downloads-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Downloads</h1>
        <p className="text-gray-500 text-sm mt-1">Generate and download reports</p>
      </div>

      {/* Report Types */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Executive Summary Report */}
        <Card className="border border-gray-100 shadow-sm" data-testid="executive-report-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <FileText size={20} className="text-[#8c3add]" />
              Executive Summary Report
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500">
              High-level summary for C-Suite executives including risk posture,
              top departments, trends, and recommendations.
            </p>

            {/* Preview */}
            <div className="p-4 bg-gray-50 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Overall Risk</span>
                <Badge className={
                  executiveSummary?.overall_risk_posture?.risk_level === "Low"
                    ? "bg-[#1fa7a1]/10 text-[#1fa7a1]"
                    : executiveSummary?.overall_risk_posture?.risk_level === "High"
                      ? "bg-red-100 text-red-700"
                      : "bg-amber-100 text-amber-700"
                }>
                  {executiveSummary?.overall_risk_posture?.risk_level || "Medium"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Users</span>
                <span className="font-semibold">{executiveSummary?.overall_risk_posture?.total_users || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Click Rate</span>
                <span className="font-semibold text-[#f59e0b]">{executiveSummary?.overall_risk_posture?.overall_click_rate || 0}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Trend</span>
                <span className="font-semibold text-[#1fa7a1] flex items-center gap-1">
                  <TrendingUp size={14} />
                  {executiveSummary?.trend_analysis?.direction || "Improving"}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                className="flex-1 bg-[#8c3add] hover:bg-[#702eb0] gap-2"
                onClick={generatePDF}
                disabled={generating}
                data-testid="generate-pdf-btn"
              >
                {generating ? (
                  <>
                    <CulsightLoader variant="inline" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Generate PDF
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Campaign Data Export */}
        <Card className="border border-gray-100 shadow-sm" data-testid="campaign-export-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 size={20} className="text-[#1fa7a1]" />
              Campaign Data Export
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500">
              Export detailed campaign data including all metrics,
              user groups, and performance statistics.
            </p>

            <div className="p-4 bg-gray-50 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Format</span>
                <Badge variant="outline">CSV</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Includes</span>
                <span className="text-sm">All campaigns</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Columns</span>
                <span className="text-sm">9 fields</span>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={downloadCSV}
              data-testid="download-csv-btn"
            >
              <Download size={16} />
              Download CSV
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <Card className="border border-gray-100 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Report Preview - Key Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total Users", value: executiveSummary?.overall_risk_posture?.total_users || 0, icon: Users, color: "#8c3add" },
              { label: "Campaigns", value: executiveSummary?.overall_risk_posture?.total_campaigns || 0, icon: PieChart, color: "#1fa7a1" },
              { label: "Total Offenders", value: executiveSummary?.offender_summary?.total_offenders || 0, icon: AlertTriangle, color: "#f59e0b" },
              { label: "High Risk", value: executiveSummary?.offender_summary?.high_risk_count || 0, icon: Shield, color: "#ef4444" }
            ].map((stat, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${stat.color}15` }}>
                    <stat.icon size={18} style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1f1f23]">{stat.value}</p>
                    <p className="text-xs text-gray-500">{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Risk Departments Preview */}
      <Card className="border border-gray-100 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Top Risk Departments</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Department</th>
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Risk Score</th>
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Fail Rate</th>
              </tr>
            </thead>
            <tbody>
              {(executiveSummary?.top_risk_departments || []).map((dept, index) => (
                <tr key={index} className="border-b border-gray-50">
                  <td className="py-4 px-6 font-medium text-[#1f1f23]">{dept.name}</td>
                  <td className="py-4 px-6">
                    <span className={`font-semibold ${dept.risk_score > 60 ? 'text-red-600' : dept.risk_score > 40 ? 'text-amber-600' : 'text-[#1fa7a1]'
                      }`}>
                      {dept.risk_score}%
                    </span>
                  </td>
                  <td className="py-4 px-6 text-gray-600">{(dept.fail_rate * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Downloads;